import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/Header';
import './Admin.css';

function Admin() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [annotators, setAnnotators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('requests');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [assigningTo, setAssigningTo] = useState(null);
  const [showCreateAnnotator, setShowCreateAnnotator] = useState(false);
  const [newAnnotatorEmail, setNewAnnotatorEmail] = useState('');
  const [isCreatingAnnotator, setIsCreatingAnnotator] = useState(false);

  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Check admin access
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.isAdmin)) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch pending correction requests
  const fetchPendingRequests = async () => {
    try {
      const response = await fetch('/api/corrections/admin/pending', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data.requests || []);
      } else if (response.status === 403) {
        setError('Admin access required');
      } else {
        setError('Failed to fetch pending requests');
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to fetch pending requests');
    }
  };

  // Fetch annotators
  const fetchAnnotators = async () => {
    try {
      const response = await fetch('/api/corrections/admin/annotators', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnnotators(data.annotators || []);
      }
    } catch (err) {
      console.error('Error fetching annotators:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchPendingRequests(), fetchAnnotators()]);
      setIsLoading(false);
    };

    if (isAuthenticated && user?.isAdmin) {
      loadData();
    }
  }, [isAuthenticated, user]);

  // Assign request to annotator
  const handleAssign = async (requestId, annotatorId) => {
    try {
      const response = await fetch(`/api/corrections/admin/assign/${requestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ annotatorId })
      });

      if (response.ok) {
        await fetchPendingRequests();
        setAssigningTo(null);
        setSelectedRequest(null);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to assign request');
      }
    } catch (err) {
      console.error('Error assigning request:', err);
      alert('Failed to assign request');
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      assigned: 'status-assigned',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled'
    };
    return statusClasses[status] || 'status-pending';
  };

  // Create new annotator
  const handleCreateAnnotator = async () => {
    if (!newAnnotatorEmail.trim()) {
      alert('Please enter a valid email address');
      return;
    }

    setIsCreatingAnnotator(true);

    try {
      // First, find the user ID by email (you may need to add an API endpoint for this)
      // For now, we'll use the email as the userId
      const response = await fetch('/api/corrections/admin/annotators', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: newAnnotatorEmail, // This should be the user ID, not email
          isVerified: true,
          specializations: []
        })
      });

      if (response.ok) {
        alert('Annotator created successfully!');
        setNewAnnotatorEmail('');
        setShowCreateAnnotator(false);
        await fetchAnnotators();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to create annotator');
      }
    } catch (err) {
      console.error('Error creating annotator:', err);
      alert('Failed to create annotator');
    } finally {
      setIsCreatingAnnotator(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="admin-page">
        <Header />
        <main className="admin-main">
          <div className="admin-loading">Loading...</div>
        </main>
      </div>
    );
  }

  if (!user?.isAdmin) {
    return null;
  }

  return (
    <div className="admin-page">
      <Header />
      <main className="admin-main">
        <div className="admin-container">
          <h1>Admin Panel</h1>
          <p className="admin-subtitle">Manage human correction requests and annotators</p>

          {error && <div className="admin-error">{error}</div>}

          {/* Tabs */}
          <div className="admin-tabs">
            <button
              className={`admin-tab ${activeTab === 'requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              Correction Requests
              {pendingRequests.length > 0 && (
                <span className="tab-badge">{pendingRequests.length}</span>
              )}
            </button>
            <button
              className={`admin-tab ${activeTab === 'annotators' ? 'active' : ''}`}
              onClick={() => setActiveTab('annotators')}
            >
              Annotators
            </button>
          </div>

          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Pending Correction Requests</h2>
                <button className="refresh-btn" onClick={fetchPendingRequests}>
                  Refresh
                </button>
              </div>

              {pendingRequests.length === 0 ? (
                <div className="empty-state">
                  <p>No pending correction requests</p>
                </div>
              ) : (
                <div className="requests-table-container">
                  <table className="requests-table">
                    <thead>
                      <tr>
                        <th>Request ID</th>
                        <th>User</th>
                        <th>Duration</th>
                        <th>Cost</th>
                        <th>Status</th>
                        <th>Requested</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRequests.map(request => (
                        <tr key={request.id}>
                          <td className="request-id">{String(request.id).slice(0, 8)}...</td>
                          <td className="request-user">{request.userEmail || String(request.userId || '').slice(0, 8)}</td>
                          <td>{formatDuration(request.audioDurationSeconds)}</td>
                          <td className="request-cost">${request.cost != null ? Number(request.cost).toFixed(2) : '0.00'}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadge(request.status)}`}>
                              {request.status}
                            </span>
                          </td>
                          <td>{formatDate(request.createdAt)}</td>
                          <td>
                            {request.status === 'pending' && (
                              <button
                                className="assign-btn"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setAssigningTo(request.id);
                                }}
                              >
                                Assign
                              </button>
                            )}
                            {request.status === 'assigned' && (
                              <span className="assigned-to">
                                Assigned to: {String(request.annotatorId || '').slice(0, 8)}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Annotators Tab */}
          {activeTab === 'annotators' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Annotators</h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="refresh-btn" onClick={fetchAnnotators}>
                    Refresh
                  </button>
                  <button
                    className="create-btn"
                    onClick={() => setShowCreateAnnotator(true)}
                  >
                    + Create Annotator
                  </button>
                </div>
              </div>

              {annotators.length === 0 ? (
                <div className="empty-state">
                  <p>No annotators registered</p>
                  <p className="empty-hint">Annotators can be created from user accounts</p>
                </div>
              ) : (
                <div className="annotators-grid">
                  {annotators.map(annotator => (
                    <div key={annotator.id} className="annotator-card">
                      <div className="annotator-header">
                        <span className="annotator-name">{annotator.userEmail || String(annotator.userId || '').slice(0, 8)}</span>
                        <span className={`verified-badge ${annotator.isVerified ? 'verified' : 'unverified'}`}>
                          {annotator.isVerified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                      <div className="annotator-stats">
                        <div className="stat">
                          <span className="stat-label">Completed</span>
                          <span className="stat-value">{annotator.completedCount || 0}</span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Active</span>
                          <span className="stat-value">{annotator.activeCount || 0}</span>
                        </div>
                      </div>
                      {annotator.specializations && annotator.specializations.length > 0 && (
                        <div className="annotator-specializations">
                          {annotator.specializations.map((spec, idx) => (
                            <span key={idx} className="specialization-tag">{spec}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Create Annotator Modal */}
          {showCreateAnnotator && (
            <div className="modal-overlay" onClick={() => setShowCreateAnnotator(false)}>
              <div className="assignment-modal" onClick={e => e.stopPropagation()}>
                <h3>Create New Annotator</h3>
                <p>Enter the user ID of the person you want to make an annotator:</p>

                <div className="form-group">
                  <label>User ID:</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter user ID (numeric)"
                    value={newAnnotatorEmail}
                    onChange={(e) => setNewAnnotatorEmail(e.target.value)}
                  />
                  <small className="form-hint">
                    Note: You need the numeric user ID from the database.
                    They will be created as a verified annotator.
                  </small>
                </div>

                <div className="modal-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setShowCreateAnnotator(false);
                      setNewAnnotatorEmail('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="create-submit-btn"
                    onClick={handleCreateAnnotator}
                    disabled={isCreatingAnnotator || !newAnnotatorEmail.trim()}
                  >
                    {isCreatingAnnotator ? 'Creating...' : 'Create Annotator'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Assignment Modal */}
          {assigningTo && selectedRequest && (
            <div className="modal-overlay" onClick={() => setAssigningTo(null)}>
              <div className="assignment-modal" onClick={e => e.stopPropagation()}>
                <h3>Assign Correction Request</h3>
                <p>Select an annotator to handle this request:</p>

                <div className="request-details">
                  <p><strong>Duration:</strong> {formatDuration(selectedRequest.audioDurationSeconds)}</p>
                  <p><strong>Cost:</strong> ${selectedRequest.cost != null ? Number(selectedRequest.cost).toFixed(2) : '0.00'}</p>
                  <p><strong>User:</strong> {selectedRequest.userEmail || 'Unknown'}</p>
                </div>

                {annotators.filter(a => a.isVerified).length === 0 ? (
                  <p className="no-annotators-warning">No verified annotators available</p>
                ) : (
                  <div className="annotator-select-list">
                    {annotators.filter(a => a.isVerified).map(annotator => (
                      <button
                        key={annotator.id}
                        className="annotator-select-btn"
                        onClick={() => handleAssign(selectedRequest.id, annotator.id)}
                      >
                        <span>{annotator.userEmail || annotator.userId}</span>
                        <span className="select-indicator">Select</span>
                      </button>
                    ))}
                  </div>
                )}

                <button className="cancel-btn" onClick={() => setAssigningTo(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Admin;
